// "Verlauf": every habit's last weeks at a glance; a card opens its calendar.
import { ChevronRight, Flame, Target, TrendingUp, Trophy } from 'lucide-react';
import { useMemo } from 'react';
import type { Habit } from '../../shared/api';
import { completionRate, scheduleLabel, streaks, weekCount } from '../../shared/schedule';
import { EmojiTile, HabitGlyph } from '../components/EmojiTile';
import { EmptyState } from '../components/EmptyState';
import { Heatmap } from '../components/Heatmap';
import { Link } from '../components/Link';
import { SkeletonList } from '../components/Skeleton';
import { percent, streakText } from '../lib/format';
import { paths } from '../lib/router';
import { daysOf, useStore, type DoneMap } from '../lib/store';
import { useMediaQuery } from '../lib/useMediaQuery';
import { useToday } from '../lib/useToday';

function HabitHistoryCard({ habit, done, today, weeks }: { habit: Habit; done: DoneMap; today: string; weeks: number }) {
  const days = daysOf(done, habit.id);
  const streak = useMemo(() => streaks(habit, days, today), [habit, days, today]);
  const rate = useMemo(() => completionRate(habit, days, today), [habit, days, today]);
  return (
    <li>
      <Link to={paths.habit(habit.id)} className={`hist glass hc-${habit.color}${habit.archived ? ' hist--paused' : ''}`}>
        <div className="hist__head">
          <EmojiTile emoji={habit.emoji} sticker={habit.sticker} color={habit.color} size="sm" />
          <div className="hist__title">
            <span className="hist__name">{habit.name}</span>
            <span className="hist__meta">{habit.archived ? 'Pausiert' : scheduleLabel(habit.schedule)}</span>
          </div>
          {streak.current > 0 && (
            <span className="hist__streak" title="Aktuelle Serie">
              <Flame size={15} aria-hidden="true" />
              {streak.current}
              <span className="sr-only"> {streak.unit === 'weeks' ? 'Wochen' : 'Tage'} in Folge</span>
            </span>
          )}
          <ChevronRight size={18} className="hist__chevron" aria-hidden="true" />
        </div>
        <Heatmap habit={habit} days={days} today={today} weeks={weeks} />
        <div className="hist__foot">
          <span>
            Quote 30 Tage <strong>{percent(rate.ratio)}</strong>
          </span>
          <span>
            Beste Serie <strong>{streakText(streak.best, streak.unit)}</strong>
          </span>
        </div>
      </Link>
    </li>
  );
}

export function HistoryView() {
  const { habits, done, loaded } = useStore();
  const today = useToday();
  const wide = useMediaQuery('(min-width: 640px)');
  const weeks = wide ? 22 : 15;
  const active = habits.filter((h) => !h.archived);
  const paused = habits.filter((h) => h.archived);

  const summary = useMemo(() => {
    let thisWeek = 0;
    let achieved = 0;
    let expected = 0;
    let best: { habit: Habit; count: number; unit: 'days' | 'weeks' } | null = null;
    for (const habit of active) {
      const days = daysOf(done, habit.id);
      thisWeek += weekCount(days, today);
      const rate = completionRate(habit, days, today);
      achieved += rate.achieved;
      expected += rate.expected;
      const s = streaks(habit, days, today);
      if (s.current > 0 && (!best || s.current > best.count)) best = { habit, count: s.current, unit: s.unit };
    }
    return { thisWeek, ratio: expected > 0 ? achieved / expected : null, best };
  }, [active, done, today]);

  return (
    <div className="view">
      <header className="page-head">
        <div className="page-head__text">
          <p className="eyebrow">Rückblick</p>
          <h1 className="page-title">Verlauf</h1>
          <p className="page-sub">Deine letzten {weeks} Wochen auf einen Blick. Tippe ein Habit an für den Kalender.</p>
        </div>
      </header>

      {!loaded && <SkeletonList rows={2} />}
      {loaded && habits.length === 0 && <EmptyState />}

      {loaded && active.length > 0 && (
        <div className="summary">
          <div className="stat glass">
            <span className="stat__label">
              <Target size={15} aria-hidden="true" /> Diese Woche
            </span>
            <span className="stat__value">
              {summary.thisWeek}
              <span className="stat__unit">× erledigt</span>
            </span>
          </div>
          <div className="stat glass">
            <span className="stat__label">
              <TrendingUp size={15} aria-hidden="true" /> Quote 30 Tage
            </span>
            <span className="stat__value">{percent(summary.ratio)}</span>
          </div>
          <div className="stat glass stat--wide-mobile">
            <span className="stat__label">
              <Trophy size={15} aria-hidden="true" /> Längste Serie
            </span>
            <span className="stat__value">
              {summary.best ? (
                <>
                  <span className="stat__emoji" aria-hidden="true">
                    <HabitGlyph emoji={summary.best.habit.emoji} sticker={summary.best.habit.sticker} />
                  </span>
                  {summary.best.count}
                  <span className="stat__unit">{summary.best.unit === 'weeks' ? 'Wochen' : 'Tage'}</span>
                </>
              ) : (
                '–'
              )}
            </span>
          </div>
        </div>
      )}

      {loaded && active.length > 0 && (
        <ul className="hist-list">
          {active.map((habit) => (
            <HabitHistoryCard key={habit.id} habit={habit} done={done} today={today} weeks={weeks} />
          ))}
        </ul>
      )}

      {loaded && paused.length > 0 && (
        <>
          <h2 className="section-title">Pausiert</h2>
          <ul className="hist-list">
            {paused.map((habit) => (
              <HabitHistoryCard key={habit.id} habit={habit} done={done} today={today} weeks={weeks} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
